import { QueryBuilder } from '../QuerySystem';
import { EntityBuilder } from './EntityBuilder';
import { SceneBuilder } from './SceneBuilder';
import { ComponentBuilder } from './ComponentBuilder';
import { EntityBatchOperator } from './EntityBatchOperator';
/**
 * ECS流式API主入口
 * 提供统一的流式接口
 */
export class ECSFluentAPI {
    constructor(scene, querySystem, eventSystem) {
        this.scene = scene;
        this.querySystem = querySystem;
        this.eventSystem = eventSystem;
    }
    /**
     * 创建实体构建器
     * @returns 实体构建器
     */
    createEntity() {
        return new EntityBuilder(this.scene, this.scene.componentStorageManager);
    }
    /**
     * 创建场景构建器
     * @returns 场景构建器
     */
    createScene() {
        return new SceneBuilder();
    }
    /**
     * 创建组件构建器
     * @param componentClass 组件类
     * @param args 构造参数
     * @returns 组件构建器
     */
    createComponent(componentClass, ...args) {
        return new ComponentBuilder(componentClass, ...args);
    }
    /**
     * 创建查询构建器
     * @returns 查询构建器
     */
    query() {
        return new QueryBuilder(this.querySystem);
    }
    /**
     * 查找实体（简化版）
     * @param componentTypes 组件类型
     * @returns 实体数组
     */
    find(...componentTypes) {
        return this.querySystem.queryAll(...componentTypes).entities;
    }
    /**
     * 查找第一个匹配的实体
     * @param componentTypes 组件类型
     * @returns 实体或null
     */
    findFirst(...componentTypes) {
        const result = this.querySystem.queryAll(...componentTypes);
        return result.entities.length > 0 ? result.entities[0] : null;
    }
    /**
     * 按名称查找实体
     * @param name 实体名称
     * @returns 实体或null
     */
    findByName(name) {
        return this.scene.findEntity(name);
    }
    /**
     * 按标签查找实体
     * @param tag 标签
     * @returns 实体数组
     */
    findByTag(tag) {
        return this.scene.findEntitiesByTag(tag);
    }
    /**
     * 触发事件
     * @param eventType 事件类型
     * @param event 事件数据
     */
    emit(eventType, event) {
        this.eventSystem.emitSync(eventType, event);
    }
    /**
     * 异步触发事件
     * @param eventType 事件类型
     * @param event 事件数据
     */
    async emitAsync(eventType, event) {
        await this.eventSystem.emit(eventType, event);
    }
    /**
     * 监听事件
     * @param eventType 事件类型
     * @param handler 事件处理器
     * @returns 监听器ID
     */
    on(eventType, handler) {
        return this.eventSystem.on(eventType, handler);
    }
    /**
     * 一次性监听事件
     * @param eventType 事件类型
     * @param handler 事件处理器
     * @returns 监听器ID
     */
    once(eventType, handler) {
        return this.eventSystem.once(eventType, handler);
    }
    /**
     * 移除事件监听器
     * @param eventType 事件类型
     * @param listenerId 监听器ID
     */
    off(eventType, listenerId) {
        this.eventSystem.off(eventType, listenerId);
    }
    /**
     * 批量操作实体
     * @param entities 实体数组
     * @returns 批量操作器
     */
    batch(entities) {
        return new EntityBatchOperator(entities);
    }
    /**
     * 获取场景统计信息
     * @returns 统计信息
     */
    getStats() {
        return {
            entityCount: this.scene.entities.count,
            systemCount: this.scene.systems.length,
            componentStats: this.scene.componentStorageManager.getAllStats(),
            queryStats: this.querySystem.getStats(),
            eventStats: this.eventSystem.getStats()
        };
    }
}
/**
 * 创建ECS流式API实例
 * @param scene 场景
 * @param querySystem 查询系统
 * @param eventSystem 事件系统
 * @returns ECS流式API实例
 */
export function createECSAPI(scene, querySystem, eventSystem) {
    return new ECSFluentAPI(scene, querySystem, eventSystem);
}
/**
 * 全局ECS流式API实例（需要在使用前初始化）
 */
export let ECS;
/**
 * 初始化全局ECS API
 * @param scene 场景
 * @param querySystem 查询系统
 * @param eventSystem 事件系统
 */
export function initializeECS(scene, querySystem, eventSystem) {
    ECS = createECSAPI(scene, querySystem, eventSystem);
}
//# sourceMappingURL=ECSFluentAPI.js.map